import {di, GlobalEventProvider} from "@dp-instant/core";
import {log} from "@dp-instant/utils";
import {Building} from "@game/coregame/ecs/entities/Building";
import {IBuilding} from "@game/coregame/interfaces/IBuilding";
import {LevelDataState} from "@game/coregame/interfaces/LevelDataState";
import {injectable} from "inversify";
import {LevelData} from "../interfaces/LevelData";
import {PlayerType} from "../interfaces/PlayerType";
import {Population} from "@game/coregame/view/Population";
import {FighterGroupsSystem} from "@game/coregame/ecs/systems/FighterGroupSystem";
import {TimeTrack} from "@game/coregame/utils/TimeTrack";
import {CTXLevelProgress} from "../interfaces/CTXLevelProgress";
import {getOwnerGenerator} from "../helpers";

@injectable()
export class ContinentModel extends GlobalEventProvider {
    public readonly parsed: Map<string, IBuilding> = new Map();
    public readonly buildings: Map<string, Building> = new Map<string, Building>();
    private _data: LevelData;
    private _stageScores: number[] = [];
    private _stageLevel = 0;
    public readonly time: TimeTrack = new TimeTrack(1000);

    public get data(): LevelData {
        return this._data;
    }

    init(data: LevelData): this {
        this._data = data;

        const parseState = (
            state: LevelDataState,
            stage = 0,
            ownerGenerator: (color: string | null) => PlayerType
        ) => {
            const {shapes} = state;

            if (shapes) {
                const startOwner = ownerGenerator(state.fillColor);
                this.parsed.set(`${data.id}-${state.id}`, {
                    stage,
                    shapes,
                    shapePos: [NaN, NaN],
                    statePos: [state.x, state.y],
                    stateRadius: state.radius || 40,
                    startOwner
                });
            } else {
                log.warn("Failed to parse", state.id);
            }
        };
        for (let i = 0; i < this._data.stages.length; i++) {
            const ownerGenerator = getOwnerGenerator();
            this._data.stages[i].states.forEach(state => parseState(state, i, ownerGenerator));
        }
        this._stageScores = this._data.stages.map(_ => 0);
        return this;
    }
    captureStage() {
        this.time.stop();
        this._stageScores[this._stageLevel] = this.getStageScore();
        this._stageLevel++;
    }

    get isFinished(): boolean {
        return this._stageLevel > this._data.stages.length - 1;
    }
    get stageLevel(): number {
        return this._stageLevel;
    }
    get totalStages(): number {
        return this._data.stages.length;
    }

    set stageLevel(value: number) {
        this._stageLevel = value;
    }
    getOrCreateBuildingEntity(id: string, data: IBuilding): Building {
        if (!this.buildings.has(id)) {
            this.buildings.set(id, di.get<Building>(Building).init(id, data));
        }
        return this.buildings.get(id)!;
    }
    public getTotalScore(): number {
        return this._stageScores.reduce((p, c) => p + c);
    }
    public getStageScore(stage?: number): number {
        const t = this.time.getTotalTime() * 0.001;
        let totalUnits = 0;
        let totalBuildings = 0;
        this.buildings.forEach(building => {
            if (building.owner === PlayerType.First) {
                totalBuildings++;
                totalUnits += building.get(Population)?.current || 0;
            }
        });
        const groups = FighterGroupsSystem.GetActiveGroups();
        for (const group of groups) {
            if (group.Owner === PlayerType.First) {
                totalUnits += group.Amount;
            }
        }
        const score = (totalBuildings * totalUnits) / t;
        // console.group("endLevel");
        // console.log("Total time", t);
        // console.log("totalUnits", totalUnits);
        // console.log("totalBuildings", totalBuildings);
        // console.log("Score", score);
        // console.groupEnd();
        return Math.round(score);
    }
    public getHistory(): CTXLevelProgress {
        return {
            l: this.data.id,
            c: this.stageLevel,
            s: this._stageScores
        };
    }
    public dispose() {
        this.buildings.forEach(building => building.destroy());
        this.buildings.clear();
        this.parsed.clear();
        this.time.stop();
    }
}
